<template>
  <div class="szw-public font-sans text-[#191c1d]">

    <!-- Hero -->
    <section class="szw-public__hero relative overflow-hidden">

      <div class="absolute inset-0 pointer-events-none">
        <div class="absolute inset-0 bg-[#f8f9fa]"></div>
        <svg class="absolute top-0 right-0 w-2/3 h-full translate-x-1/4 -translate-y-1/4" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,1000 C300,800 400,1000 700,800 C1000,600 1000,0 1000,0 L0,0 Z" fill="#071c5b" opacity="0.2"/>
        </svg>
        <svg class="absolute bottom-0 left-0 w-full h-2/3" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,1000 C200,900 400,1000 600,800 C800,600 1000,700 1000,500 L1000,1000 Z" fill="#006e29" opacity="0.15"/>
        </svg>
        <div class="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#f5c343] opacity-20 blur-[100px]"></div>
        <div class="absolute bottom-20 right-20 w-80 h-80 rounded-full bg-[#d24340] opacity-[0.12] blur-[100px]"></div>
      </div>

      <div class="relative z-10 max-w-6xl mx-auto px-6 py-24 md:py-36">

        <!-- Logo -->
        <div class="mb-10">
          <img src="/images/static-home-page/samergo/zhe-bar.png" alt="ZHE-BAR Zorg &amp; Welzijn" class="h-14 w-auto"/>
        </div>

        <!-- Headline -->
        <h1 class="text-[clamp(2.2rem,6vw,4rem)] font-extrabold text-[#071c5b] leading-[1.1] tracking-tight mb-6 max-w-3xl">
          {{ $t('samergo_public_hero_title_1') }}<br/>
          <span class="szw-headline-accent">{{ $t('samergo_public_hero_title_2') }}</span>
        </h1>
        <p class="text-[#454650] text-lg md:text-xl font-light leading-relaxed mb-14 max-w-xl">
          {{ $t('samergo_public_hero_subtitle') }}
        </p>

        <!-- Two CTA cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl">
          <!-- Patient -->
          <a
            href="https://zhebar.nl"
            target="_blank"
            rel="noopener"
            class="szw-cta-card szw-cta-card--patient group"
          >
            <div class="szw-cta-card__icon-wrap bg-[#eafff0]">
              <span class="material-symbols-outlined text-[#006e29]" style="font-size: 28px;">person</span>
            </div>
            <div class="szw-cta-card__text">
              <span class="szw-cta-card__label text-[#191c1d]">{{ $t('samergo_public_cta_patient_label') }}</span>
              <span class="szw-cta-card__sub">{{ $t('samergo_public_cta_patient_sub') }}</span>
            </div>
            <span class="material-symbols-outlined szw-cta-card__arrow text-[#c6c5d2]">arrow_forward</span>
          </a>

          <!-- Professional -->
          <a
            @click="$router.push('/login')"
            class="szw-cta-card szw-cta-card--pro group"
          >
            <div class="szw-cta-card__icon-wrap bg-[#dde1ff]">
              <span class="material-symbols-outlined text-[#071c5b]" style="font-size: 28px;">badge</span>
            </div>
            <div class="szw-cta-card__text">
              <span class="szw-cta-card__label text-[#191c1d]">{{ $t('samergo_public_cta_pro_label') }}</span>
              <span class="szw-cta-card__sub">{{ $t('samergo_public_cta_pro_sub') }}</span>
            </div>
            <span class="material-symbols-outlined szw-cta-card__arrow text-[#c6c5d2]">arrow_forward</span>
          </a>
        </div>

      </div>

      <!-- Bottom carnival strip -->
      <div class="szw-carnival-strip absolute bottom-0 left-0 right-0 h-[3px] pointer-events-none"></div>
    </section>

  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'

  export default defineComponent({
    name: 'StaticHomeSamergo',
  })
</script>

<style scoped lang="scss">
  .szw-public {
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    min-height: calc(100vh - 75px);
  }

  .szw-public__hero {
    min-height: calc(100vh - 75px);
    display: flex;
    align-items: center;
  }

  // ── Headline accent ──────────────────────────────────────────────────────────

  .szw-headline-accent {
    background: linear-gradient(120deg, #006e29 0%, #f5c343 50%, #006e29 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shine 4s linear infinite;
  }

  @keyframes shine {
    to {
      background-position: 200% center;
    }
  }

  // ── CTA cards ────────────────────────────────────────────────────────────────

  .szw-cta-card {
    display: flex;
    align-items: center;
    gap: 16px;
    background: white;
    border: 1px solid #e2e4e9;
    border-radius: 20px;
    padding: 22px 24px;
    text-decoration: none;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
    transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s;
    cursor: pointer;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(120deg, transparent 30%, rgba(0, 0, 0, 0.025) 50%, transparent 70%);
      transform: translateX(-100%);
      transition: transform 0.5s ease;
    }

    &:hover::before {
      transform: translateX(100%);
    }

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.10);
    }

    &--patient {
      border-top: 3px solid #7df98f;
    }

    &--pro {
      border-top: 3px solid #dde1ff;
    }

    &--patient:hover {
      border-color: #7df98f;
    }

    &--pro:hover {
      border-color: #8b90ff;
    }

    &--patient:hover .szw-cta-card__arrow {
      color: #006e29;
    }

    &--pro:hover .szw-cta-card__arrow {
      color: #3f4cce;
    }
  }

  .szw-cta-card__icon-wrap {
    width: 54px;
    height: 54px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: transform 0.2s;

    .szw-cta-card:hover & {
      transform: scale(1.08) rotate(-3deg);
    }
  }

  .szw-cta-card__text {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .szw-cta-card__label {
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.3;
  }

  .szw-cta-card__sub {
    font-size: 0.8rem;
    color: #757681;
  }

  .szw-cta-card__arrow {
    font-size: 20px;
    transition: color 0.2s, transform 0.2s;
    flex-shrink: 0;

    .szw-cta-card:hover & {
      transform: translateX(4px);
    }
  }

  // ── Bottom carnival strip ────────────────────────────────────────────────────

  .szw-carnival-strip {
    background: linear-gradient(90deg,
    #7df98f 0%, #7df98f 16.6%,
    #f5c343 16.6%, #f5c343 33.2%,
    #dde1ff 33.2%, #dde1ff 49.8%,
    #7df98f 49.8%, #7df98f 66.4%,
    #f5c343 66.4%, #f5c343 83%,
    #dde1ff 83%, #dde1ff 100%
    );
    background-size: 200% 100%;
    animation: carnival-slide 8s linear infinite;
  }

  @keyframes carnival-slide {
    from {
      background-position: 0 0;
    }
    to {
      background-position: 200% 0;
    }
  }
</style>
